import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
} from '@mui/material';

import 'dayjs/locale/ko';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DateAdapter from '@mui/lab/AdapterDateFns';
import * as dayjs from 'dayjs'

// 날짜칸 보조문구 - 날짜 세그먼트(연/월/일)를 따로따로 입력해야 한다. 한 번에 "11302026"처럼
// 붙여 입력하면 맨 앞 4자리(1130)가 "연"으로 들어가버려 "1130-02-26"처럼 엉뚱하게 파싱된다
// (2026-10-20 점검 이슈 #8). format이 YYYY-MM-DD(연이 맨 앞)라서 생기는 문제라, 코드로 막기보다
// 안내 문구로 알려준다.
const DATE_HELPER_TEXT = '연(4자리)·월(2자리)·일(2자리) 순서로 한 칸씩 입력하세요(예: 2026 enter 11 enter 30)';

// 결재 숨은 위저드(2026-10-20 점검 이슈 #8) - "완료 여부" 체크박스가 계속 비활성으로 보여
// 막힌 것처럼 느껴지던 문제. activeStep이 몇 단계인지, 지금 무엇을 해야 다음 칸이 열리는지
// 화면에 그대로 보여준다.
const STEP_GUIDE = {
  0: { severity: 'info', text: '아직 발행요청 전입니다 - 세금계산서 관리 화면에서 발행요청을 먼저 진행하세요.' },
  1: { severity: 'info', text: '발행요청까지 완료됐고, 발행완료 처리를 기다리는 중입니다.' },
  2: { severity: 'warning', text: '1단계: 지급종류·수금(지급)예정일을 입력하고 "수금(지급)요청 여부"를 체크한 뒤 저장하세요. 저장해야 아래 등록 버튼이 열립니다.' },
  3: { severity: 'warning', text: '2단계: 아래 "수금(지급) 등록" 버튼으로 실제 들어온(또는 나간) 금액을 기록하세요. 전액이 모이면 자동으로 완료 처리됩니다.' },
  4: { severity: 'success', text: '수금(지급) 완료까지 끝났습니다. 실적 확정은 세금계산서 관리 화면에서 진행합니다.' },
  5: { severity: 'success', text: '실적 확정까지 완료된 건입니다.' },
};

// 수금(지급) 등록/취소 - 레거시 단일 PROC_COMP_YN/PROC_COMP_DT 플래그를 여기서 직접 입력받지
// 않는다. 2026-10-21부터 BillCollectAddDialog(BillCollectService.addCollect)로 기록하면
// 서버(syncInvoiceStatus)가 두 플래그를 파생시켜준다 - 화면마다 따로 계산하면 숫자가 갈라진다
// (설계: obsidian bito/수금·지급 계획 N개 설계(2026-10-21).md §8 "2차 릴리스 TODO" 반영).
const TaxBillColAndPayForm = ({ activeStep, formik, onOpenCollect, onOpenCancelCollection }) => {
  const guide = STEP_GUIDE[activeStep];
  const isCompleted = Boolean(formik.values.procCompYn);

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        my: 2,
        px: 2,
      }}
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
    >
      {guide && (
        <Alert severity={guide.severity} sx={{ mb: 1 }}>
          {guide.text}
        </Alert>
      )}
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={1}
      >
        <Grid item xs={4}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            select
            disabled={activeStep !== 2}
            id="procTyp"
            name="procTyp"
            label="지급종류"
            value={formik.values.procTyp}
            onChange={(event) => {
              formik.setFieldValue('procTyp', event.target.value);
            }}
            error={formik.touched.procTyp && Boolean(formik.errors.procTyp)}
            helperText={formik.touched.procTyp && formik.errors.procTyp}
          >
            <MenuItem value="일반">일반</MenuItem>
            <MenuItem value="현금">현금</MenuItem>
            <MenuItem value="기타">기타</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <DatePicker
              label="수금(지급)예정일"
              format="YYYY-MM-DD"
              mask={'____-__-__'}
              disabled={activeStep !== 2}
              id="procPlanDt"
              name="procPlanDt"
              value={dayjs(formik.values.procPlanDt) || ''}
              onChange={(newValue) => {
                formik.setFieldValue('procPlanDt', dayjs(newValue));
              }}
              slotProps={{
                textField: {
                  margin: "normal",
                  variant: "outlined",
                  error:
                    formik.touched.procPlanDt &&
                    Boolean(formik.errors.procPlanDt),
                  helperText:
                    (formik.touched.procPlanDt && formik.errors.procPlanDt) ||
                    (activeStep === 2 ? DATE_HELPER_TEXT : ' '),
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={4}>
          <FormGroup>
            <FormControlLabel
              id="procPlanYn"
              name="procPlanYn"
              label="수금(지급)요청 여부"
              disabled={activeStep !== 2}
              control={
                <Checkbox
                  checked={formik.values.procPlanYn}
                  onChange={formik.handleChange}
                />
              }
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={1}
      >
        <Grid item xs={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <DatePicker
              label="수금(지급)일 (최근 등록일, 조회만)"
              format="YYYY-MM-DD"
              mask={'____-__-__'}
              disabled
              id="procCompDt"
              name="procCompDt"
              value={formik.values.procCompDt ? dayjs(formik.values.procCompDt) : null}
              onChange={() => {}}
              slotProps={{
                textField: {
                  margin: "normal",
                  variant: "outlined",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={4}>
          <FormGroup>
            <FormControlLabel
              id="procCompYn"
              name="procCompYn"
              disabled
              label="수금(지급) 완료 여부 (조회만)"
              control={<Checkbox checked={isCompleted} />}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={4}></Grid>
      </Grid>
      {activeStep === 2 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            my: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </Box>
      )}
      {(activeStep === 3 || isCompleted) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            my: 2,
          }}
        >
          {isCompleted && (
            <Button variant="outlined" color="warning" onClick={onOpenCancelCollection}>
              수금(지급) 취소
            </Button>
          )}
          {activeStep === 3 && (
            <Button variant="contained" onClick={onOpenCollect}>
              수금(지급) 등록
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TaxBillColAndPayForm;
